import React, { useMemo } from 'react';
import { Spin, Tooltip as AntTooltip } from 'antd';
import clsx from 'clsx';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import {
	NEGATIVE_CHANGE_COLOR,
	POSITIVE_CHANGE_COLOR,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';
import { roundToOneDecimalPlace } from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
	formatComparisonRangeLabel,
	getComparisonSeriesLabel,
} from '../../utils/AnalyticsComparisonUtils';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	PriorityStackedBarChart,
} from '../analytics_charts';
import { StackedBarChart } from '../charts/StackedBarChart';
import { useAnalyticsBreakdownComparison } from '../../hooks/useAnalyticsBreakdownComparison';
import { fetchWorkOrderCountByMonthComparisonSilent } from '../../thunks/work_analytics_thunks';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { ORDERED_STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { escapeHtml } from '../../utils/AnalyticsComparisonTooltipUtils';

const DataSet = require('@antv/data-set');

type RawMonthRow = {
	yearMonthKey: string;
	countByPriority: Array<{
		workOrderCount: number;
		workOrderPriorityId?: number | string;
		workOrderPriority?: { id?: number | string; name?: string };
	}>;
};

type Priority = { id: number | string; name: string };

export type WorkPriorityChartCardProps = {
	data: RawMonthRow[];
	loading: boolean;
	priorities: Priority[];
	filters: Record<string, any>;
	userType: string;
};

const PATTERN_ID_PREFIX = 'priority-hatch';

const SHORT_MONTH_FORMAT = "MMM [']YY";

const formatMonthLabel = (yearMonthKey: string) => {
	if (!yearMonthKey) return '';
	const parsed = moment(yearMonthKey, 'YYYY-MM');
	return parsed.isValid() ? parsed.format(SHORT_MONTH_FORMAT) : yearMonthKey;
};

const formatMonthFull = (yearMonthKey: string) => {
	if (!yearMonthKey) return '';
	const parsed = moment(yearMonthKey, 'YYYY-MM');
	return parsed.isValid() ? parsed.format('MMMM YYYY') : yearMonthKey;
};

const toFlatRows = (
	monthRows: RawMonthRow[],
	priorities: Priority[]
): Array<{
	monthKey: string;
	monthLabel: string;
	monthFull: string;
	priority: string;
	value: number;
}> => {
	const orderedPriorityNames = priorities.map((p) => p.name);
	const flat: Array<any> = [];
	(monthRows || []).forEach((row) => {
		const monthKey = row.yearMonthKey;
		const monthLabel = formatMonthLabel(monthKey);
		const monthFull = formatMonthFull(monthKey);
		const byName: Record<string, number> = {};
		(row.countByPriority || []).forEach((c) => {
			const name = (c.workOrderPriority && c.workOrderPriority.name) || '';
			if (name) byName[name] = (c.workOrderCount || 0) + (byName[name] || 0);
		});
		orderedPriorityNames.forEach((priorityName) => {
			flat.push({
				monthKey,
				monthLabel,
				monthFull,
				priority: priorityName,
				value: byName[priorityName] || 0,
			});
		});
	});
	return flat;
};

const computeMonthTotal = (
	rows: Array<{ monthKey: string; value: number }>
): Map<string, number> => {
	const totals = new Map<string, number>();
	rows.forEach((row) => {
		totals.set(row.monthKey, (totals.get(row.monthKey) || 0) + row.value);
	});
	return totals;
};

const WorkPriorityChartCard = ({
	data,
	loading,
	priorities,
	filters,
	userType,
}: WorkPriorityChartCardProps) => {
	const priorityNames = useMemo(() => priorities.map((p) => p.name), [priorities]);
	const stackColorMap = useMemo(() => {
		const map: Record<string, string> = {};
		priorityNames.forEach((name, index) => {
			map[name] = ORDERED_STOPLIGHT_COLORS[index % ORDERED_STOPLIGHT_COLORS.length];
		});
		return map;
	}, [priorityNames]);

	const {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison,
	} = useAnalyticsBreakdownComparison({
		filters,
		fetchComparisonData: fetchWorkOrderCountByMonthComparisonSilent(userType),
	});

	const currentFlatRows = useMemo(() => toFlatRows(data || [], priorities), [data, priorities]);
	const previousFlatRows = useMemo(
		() => toFlatRows(comparisonRows || [], priorities),
		[comparisonRows, priorities]
	);

	// Pair current ↔ previous rows by the *calendar offset* between the selected and the
	// comparison range start, not by array index. The backend can omit empty months from
	// either response, so an index-based pairing maps the wrong previous month onto each
	// current month whenever the responses don't line up 1-for-1. This mirrors the
	// `pairComparisonByMonth` helper used by lifecycle and KPI charts.
	const chartData = useMemo(() => {
		const currentTagged = currentFlatRows.map((row) => ({
			...row,
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
		}));
		if (!hasComparison) return currentTagged;

		const currentMonthCount = (data || []).length;

		// Index every previous-period month by its own yearMonthKey so we can look up the
		// canonical match for each current month (e.g. for VS LAST YEAR the previous Apr 2025
		// row is indexed at "2025-4", and Apr 2026's expected key is also "2025-4").
		const previousByMonthKey = new Map<string, RawMonthRow>();
		(comparisonRows || []).forEach((row) => {
			if (row && row.yearMonthKey) {
				previousByMonthKey.set(String(row.yearMonthKey), row);
			}
		});

		// Compute the month offset between the two range starts. `1` for VS LAST YEAR-style
		// year-shifted comparisons resolves to 12 months; a Previous Period comparison gives
		// you whatever the period length is in months.
		const monthOffset =
			selectedStart && comparisonRange && comparisonRange[0]
				? selectedStart.diff(comparisonRange[0], 'months')
				: 0;

		const comparisonMonthRows = comparisonRows || [];

		const lookupPreviousMonth = (
			currentYearMonthKey: string,
			index: number
		): RawMonthRow | null => {
			if (!currentYearMonthKey) return null;
			if (monthOffset > 0) {
				const curDate = moment(`${currentYearMonthKey}-01`, 'YYYY-M-DD');
				if (curDate.isValid()) {
					const prevDate = curDate.clone().subtract(monthOffset, 'months');
					const expectedKey = `${prevDate.year()}-${prevDate.month() + 1}`;
					const exact = previousByMonthKey.get(expectedKey);
					if (exact) return exact;
				}
			} else {
				const byIndex = comparisonMonthRows[index];
				if (byIndex) return byIndex;
			}
			return null;
		};

		const previousTagged: Array<any> = [];
		(data || []).forEach((curMonth, index) => {
			const currentMonthLabel = formatMonthLabel(curMonth.yearMonthKey);
			const currentMonthFull = formatMonthFull(curMonth.yearMonthKey);
			const previousMonth = lookupPreviousMonth(curMonth.yearMonthKey, index);
			const previousMonthLabel = previousMonth ? formatMonthLabel(previousMonth.yearMonthKey) : '';
			const previousMonthFull = previousMonth ? formatMonthFull(previousMonth.yearMonthKey) : '';
			const previousByName: Record<string, number> = {};
			if (previousMonth) {
				(previousMonth.countByPriority || []).forEach((c) => {
					const name = (c.workOrderPriority && c.workOrderPriority.name) || '';
					if (name) previousByName[name] = c.workOrderCount || 0;
				});
			}
			priorityNames.forEach((priorityName) => {
				previousTagged.push({
					monthKey: curMonth.yearMonthKey,
					monthLabel: currentMonthLabel,
					// `monthFull` on the previous row carries the actual previous-period month so
					// the tooltip header reads the right calendar slot (e.g. "April 2025" for the
					// hatched bar that sits next to the April 2026 solid bar).
					monthFull: previousMonthFull || currentMonthFull,
					previousMonthLabel,
					previousMonthFull,
					priority: priorityName,
					value: previousByName[priorityName] || 0,
					[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS,
				});
			});
		});

		// Drop trailing months that have no current data so the chart doesn't draw empty bands.
		// Previous rows come FIRST in the array — G2's dodge layout follows data order, so this
		// pins the hatched previous bar to the LEFT of the solid current bar inside each pair.
		const usedCurrent = currentTagged.slice(0, currentMonthCount * priorityNames.length);
		return [...previousTagged, ...usedCurrent];
	}, [
		currentFlatRows,
		hasComparison,
		comparisonRows,
		data,
		priorityNames,
		selectedStart,
		comparisonRange,
	]);

	const summaryDelta = useMemo(() => {
		if (!hasComparison) return null;
		const currentTotal = (currentFlatRows || []).reduce((sum, row) => sum + (row.value || 0), 0);
		const previousTotal = (previousFlatRows || []).reduce((sum, row) => sum + (row.value || 0), 0);
		if (!previousTotal) return null;
		const pct = ((currentTotal - previousTotal) / previousTotal) * 100;
		if (!isFinite(pct) || pct === 0) return null;
		const isUp = pct > 0;
		// More work orders is not inherently "good" or "bad" — use neutral up/down semantics
		// with green for up (more activity, matches the Figma's 70.5% green).
		const color = isUp ? NEGATIVE_CHANGE_COLOR : POSITIVE_CHANGE_COLOR;
		return (
			<div
				className={clsx(
					'flex shrink-0 items-center gap-1',
					isUp ? 'text-lifecycleChange-negative' : 'text-lifecycleChange-positive'
				)}
			>
				<DeltaArrowIcon direction={isUp ? 'up' : 'down'} color={color} />
				<span className="bodyregular">{Math.abs(roundToOneDecimalPlace(pct))}%</span>
			</div>
		);
	}, [hasComparison, currentFlatRows, previousFlatRows]);

	const renderTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		// G2's `shared: true` tooltip with our dodge layout only hands back items from the
		// hovered bar's series — but we want a single tooltip that shows BOTH the current
		// and previous period side-by-side regardless of which bar the cursor is over.
		// Pull the monthKey off the hovered row, then look up the full set of rows for the
		// same month from `chartData` ourselves.
		const firstItem = items[0];
		const monthKey = firstItem && firstItem.data && firstItem.data.monthKey;
		if (!monthKey) return '';

		const rowsForMonth = (chartData || []).filter((row: any) => row && row.monthKey === monthKey);
		const currentRows = rowsForMonth.filter(
			(row: any) => row[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT
		);
		const previousRows = rowsForMonth.filter(
			(row: any) => row[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_PREVIOUS
		);

		const currentMonthFull =
			(currentRows[0] && currentRows[0].monthFull) ||
			(firstItem && firstItem.data && firstItem.data.monthFull) ||
			title;
		const previousMonthFull =
			(previousRows[0] && (previousRows[0].previousMonthFull || previousRows[0].monthFull)) || '';

		const renderRow = (row: any, isPrevious: boolean) => {
			const priorityName = row && row.priority;
			const color = stackColorMap[priorityName] || '#888888';
			const swatch = isPrevious
				? `<span style="display:inline-block;width:10px;height:10px;margin-right:6px;background:repeating-linear-gradient(-45deg, ${color} 0 2px, #ffffff 2px 4px);border:1px solid ${color};"></span>`
				: `<span style="display:inline-block;width:10px;height:10px;margin-right:6px;background:${color};border-radius:2px;"></span>`;
			return (
				`<li style="list-style:none;display:flex;justify-content:space-between;align-items:center;padding:2px 14px;">` +
				`<span style="display:inline-flex;align-items:center;font-size:12px;color:#757575;">${swatch}${escapeHtml(
					priorityName
				)}</span>` +
				`<span style="font-size:12px;color:#757575;margin-left:24px;">${
					(row && row.value) || 0
				}</span>` +
				`</li>`
			);
		};

		const renderHeader = (label: string, monthFull: string) =>
			`<div style="font-weight:500;font-size:13px;color:#262626;padding:8px 14px 4px;">${escapeHtml(
				label
			)} · ${escapeHtml(monthFull)}</div>`;

		const renderDivider = () =>
			`<div style="height:1px;background:#f0f0f0;margin:6px 14px;"></div>`;

		const sections: string[] = [];
		if (currentRows.length > 0) {
			sections.push(renderHeader('Current Period', currentMonthFull));
			sections.push(...currentRows.map((row: any) => renderRow(row, false)));
		}
		if (previousRows.length > 0) {
			if (sections.length > 0) sections.push(renderDivider());
			sections.push(
				renderHeader(getComparisonSeriesLabel(comparisonState.mode), previousMonthFull)
			);
			sections.push(...previousRows.map((row: any) => renderRow(row, true)));
		}

		return (
			`<div style="min-width:240px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">` +
			sections.join('') +
			`</div>`
		);
	};

	const handleDownload = () => {
		if (!data || data.length === 0) return;
		const includeComparison = hasComparison && (comparisonRows || []).length > 0;
		const baseHeaders = ['Month', ...priorityNames];
		const headers = includeComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'New Work Order Count by Priority',
			title: 'New Work Order Count by Priority',
			headers,
		});

		const monthRowToRecord = (row: RawMonthRow, isHistorical: boolean) => {
			const byName: Record<string, number> = {};
			(row.countByPriority || []).forEach((c) => {
				const name = (c.workOrderPriority && c.workOrderPriority.name) || '';
				if (name) byName[name] = c.workOrderCount || 0;
			});
			const record: Record<string, string | number> = {
				month: moment(row.yearMonthKey, 'YYYY-MM').isValid()
					? moment(row.yearMonthKey, 'YYYY-MM').format('MMMM YYYY')
					: row.yearMonthKey,
			};
			priorityNames.forEach((priorityName, index) => {
				record[`p${index}`] = byName[priorityName] || 0;
			});
			if (includeComparison) record.historicalData = isHistorical ? 'Yes' : 'No';
			return record;
		};

		const previousRows = includeComparison
			? (comparisonRows as RawMonthRow[]).map((row) => monthRowToRecord(row, true))
			: [];
		const currentRows = (data || []).map((row) => monthRowToRecord(row, false));

		csvExporter.generateCsv([...previousRows, ...currentRows]);
	};

	const summaryTotal = useMemo(
		() => (currentFlatRows || []).reduce((sum, row) => sum + (row.value || 0), 0),
		[currentFlatRows]
	);

	const title = (
		<AnalyticsChartHeader
			label="New Work Order Count by Priority"
			value={`${summaryTotal} WOs`}
			delta={summaryDelta}
		/>
	);

	const extra = (
		<AnalyticsChartActions
			comparison={{
				value: comparisonState || { mode: COMPARISON_MODE.NONE },
				onChange: setComparison,
				selectedStart,
				selectedEnd,
				isTimeBased: true,
				loading: !!comparisonLoading,
				disabled: !!comparisonDisabled,
				disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
			}}
			onDownload={handleDownload}
		/>
	);

	const currentRangeLabel = formatComparisonRangeLabel(selectedStart, selectedEnd);
	const previousRangeLabel = hasComparison
		? formatComparisonRangeLabel(
				comparisonRange ? comparisonRange[0] : null,
				comparisonRange ? comparisonRange[1] : null
		  )
		: '';

	// Single-mode data shape matches the production prod chart exactly: one row per
	// (yearMonthKey × priority), produced by folding `countByPriority` arrays into
	// per-priority rows. Lets us reuse the existing `StackedBarChart` engine for
	// `NO COMPARISON` so the visual matches prod without any new tweaks.
	const singleModeFoldedData = useMemo(() => {
		if (hasComparison) return null;
		if (!data || data.length === 0 || priorityNames.length === 0) return [];
		const dataSet = new DataSet();
		const view = dataSet.createView().source(data);
		view.transform({
			type: 'map',
			callback: (row: any) => {
				const nextRow = { ...row };
				priorityNames.forEach((priorityName) => {
					nextRow[priorityName] = 0;
				});
				(row.countByPriority || []).forEach((c: any) => {
					const name = nullSafeGet('workOrderPriority.name', c);
					if (name) nextRow[name] = c.workOrderCount;
				});
				return nextRow;
			},
		});
		view.transform({
			type: 'fold',
			fields: priorityNames,
			key: 'priority',
			value: 'workOrderCount',
		});
		return view;
	}, [hasComparison, data, priorityNames]);

	const singleModeChartConfig = useMemo(
		() => ({
			cols: {
				yearMonthKey: { alias: 'Month' },
				workOrderCount: { alias: '# Work Orders' },
			},
			yAxisLabel: {
				textStyle: { fontSize: '12', fontFamily: 'Roboto', fill: 'rgba(0,0,0,0.85)' },
			},
			xAxisLabel: {
				textStyle: { fontSize: '12', fontFamily: 'Roboto', fill: 'rgba(0,0,0,0.85)' },
			},
		}),
		[]
	);

	return (
		<AnalyticsChartCard title={title} extra={extra}>
			<Spin spinning={loading || comparisonLoading}>
				<div className="w-full min-w-0">
					{hasComparison ? (
						<PriorityStackedBarChart
							data={chartData}
							stackColorMap={stackColorMap}
							stackOrder={priorityNames}
							hasComparison={hasComparison}
							axisYTitle="Work Orders Created"
							renderTooltip={renderTooltip}
							patternIdPrefix={PATTERN_ID_PREFIX}
							height={400}
						/>
					) : (
						<StackedBarChart
							height={400}
							chartPadding={[40, 40, 40, 40]}
							showTooltipTitle={false}
							xAxisName="yearMonthKey"
							yAxisName="workOrderCount"
							xAxisLabel={singleModeChartConfig.xAxisLabel}
							yAxisLabel={singleModeChartConfig.yAxisLabel}
							fillArea={false}
							data={singleModeFoldedData || []}
							cols={singleModeChartConfig.cols}
							color={['priority', ORDERED_STOPLIGHT_COLORS] as any}
							position="yearMonthKey*workOrderCount"
						/>
					)}
					<div className="mt-4 flex flex-col items-center gap-2">
						{hasComparison ? (
							<div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
								{priorityNames.map((name) => (
									<span key={name} className="inline-flex items-center gap-2">
										<span
											className="inline-block h-2.5 w-2.5 rounded-[2px]"
											style={{ backgroundColor: stackColorMap[name] }}
											aria-hidden="true"
										/>
										<span className="footnoteregular text-character-secondary">{name}</span>
									</span>
								))}
							</div>
						) : null}
						{hasComparison ? (
							<div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
								<span className="inline-flex items-center gap-2">
									<span
										className="inline-block h-2.5 w-2.5 rounded-[2px] bg-grey-6"
										aria-hidden="true"
									/>
									<span className="footnoteMedium text-character-primary">Current Period</span>
									<AntTooltip title={currentRangeLabel}>
										<span className="footnoteregular truncate text-character-secondary">
											{currentRangeLabel}
										</span>
									</AntTooltip>
								</span>
								<span className="inline-flex items-center gap-2">
									<span
										className="inline-block h-2.5 w-2.5 rounded-[2px] border border-grey-5"
										style={{
											backgroundImage:
												'repeating-linear-gradient(-45deg, rgba(0,0,0,0.55) 0 1.5px, transparent 1.5px 3.5px)',
										}}
										aria-hidden="true"
									/>
									<span className="footnoteMedium text-character-primary">
										{getComparisonSeriesLabel(comparisonState.mode)}
									</span>
									<AntTooltip title={previousRangeLabel}>
										<span className="footnoteregular truncate text-character-secondary">
											{previousRangeLabel}
										</span>
									</AntTooltip>
								</span>
							</div>
						) : null}
					</div>
				</div>
			</Spin>
		</AnalyticsChartCard>
	);
};

export default WorkPriorityChartCard;
